import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Link,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { useColorModeValue } from '../../components/ui/color-mode';
import { parseReviewSinceDate } from '../../utils/stockReviewMode';
import { useStockReviewMode } from './StockReviewModeContext';

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear()).padStart(4, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isReviewModeControlVisible(pathname = '') {
  return /\/stock\/[^/]+(?:\/(?:charts|sec-filings))?\/?$/.test(pathname);
}

export default function StockReviewModeControl() {
  const { ticker: rawTicker } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const ticker = rawTicker ? rawTicker.toLowerCase() : '';
  const {
    sinceDate,
    setSinceDate,
    setEnabled,
    clear,
    isActive,
  } = useStockReviewMode();
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(sinceDate || '');
  const panelBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const today = useMemo(getTodayDateString, []);
  const validDraftDate = parseReviewSinceDate(draftDate);
  const filingsHref = ticker ? `/stock/${ticker}/sec-filings` : null;
  const stockPriceHref = ticker ? `/stock/${ticker}/charts?dataset=stockPrice` : null;

  useEffect(() => {
    if (open) {
      setDraftDate(sinceDate || '');
    }
  }, [open, sinceDate]);

  const enableReviewMode = () => {
    if (!validDraftDate) return;
    setSinceDate(validDraftDate);
    setEnabled(true);
    setOpen(false);
    if (/\/stock\/[^/]+\/?$/.test(location.pathname) && stockPriceHref) {
      navigate(stockPriceHref);
    }
  };

  const clearReviewMode = () => {
    clear();
    setDraftDate('');
    setOpen(false);
  };

  if (!isReviewModeControlVisible(location.pathname)) {
    return null;
  }

  return (
    <Box
      position="sticky"
      top={{ base: 'calc(var(--nav-h) + 64px)', md: 'calc(var(--nav-h) + 12px)' }}
      zIndex={19}
      display="flex"
      justifyContent="flex-end"
      minH="10"
      mb={2}
      pointerEvents="none"
    >
      <Box pointerEvents="auto">
        <Popover.Root
          open={open}
          onOpenChange={({ open: nextOpen }) => setOpen(nextOpen)}
          positioning={{ placement: 'bottom-end', gutter: 8 }}
        >
          <Popover.Trigger asChild>
            {isActive ? (
              <Button
                type="button"
                size="sm"
                colorPalette="blue"
                variant="subtle"
                boxShadow="sm"
              >
                <FiCalendar />
                Showing data since {sinceDate}
              </Button>
            ) : (
              <IconButton
                type="button"
                size="sm"
                variant="outline"
                bg={panelBg}
                boxShadow="sm"
                aria-label="Review since date"
              >
                <FiCalendar />
              </IconButton>
            )}
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner zIndex={50}>
              <Popover.Content
                width={{ base: 'min(320px, calc(100vw - 24px))', sm: '320px' }}
                bg={panelBg}
                borderColor={panelBorder}
                boxShadow="lg"
              >
                <Popover.Arrow>
                  <Popover.ArrowTip />
                </Popover.Arrow>
                <Popover.Header>
                  <HStack
                    justify="space-between"
                    gap={3}
                    width="full"
                    data-testid="review-mode-header"
                  >
                    <Popover.Title fontWeight="semibold">Review since date</Popover.Title>
                    {(isActive || sinceDate) && (
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        onClick={clearReviewMode}
                      >
                        Clear
                      </Button>
                    )}
                  </HStack>
                </Popover.Header>
                <Popover.Body>
                  <VStack align="stretch" gap={3}>
                    <Box>
                      <Text as="label" htmlFor="stock-review-since-date" fontSize="sm" fontWeight="medium">
                        Show new data after
                      </Text>
                      <Input
                        id="stock-review-since-date"
                        type="date"
                        value={draftDate}
                        max={today}
                        mt={1}
                        onChange={(event) => setDraftDate(event.target.value)}
                      />
                      <Text mt={1} color={mutedText} fontSize="xs">
                        Data on the selected date is not considered new.
                      </Text>
                    </Box>
                    {isActive ? (
                      <Box
                        borderTopWidth="1px"
                        borderColor={panelBorder}
                        pt={3}
                        data-testid="review-mode-destinations"
                      >
                        <Text fontSize="xs" fontWeight="semibold" color={mutedText} mb={2}>
                          Jump to what changed
                        </Text>
                        <VStack align="stretch" gap={1}>
                          {filingsHref ? (
                            <Link
                              as={RouterLink}
                              to={filingsHref}
                              fontSize="sm"
                              color="blue.600"
                              onClick={() => setOpen(false)}
                            >
                              SEC filings since {sinceDate}
                            </Link>
                          ) : null}
                          {stockPriceHref ? (
                            <Link
                              as={RouterLink}
                              to={stockPriceHref}
                              fontSize="sm"
                              color="blue.600"
                              onClick={() => setOpen(false)}
                            >
                              Stock price since {sinceDate}
                            </Link>
                          ) : null}
                        </VStack>
                      </Box>
                    ) : null}
                    <HStack justify="flex-end">
                      <Button
                        type="button"
                        size="sm"
                        colorPalette="blue"
                        disabled={!validDraftDate}
                        onClick={enableReviewMode}
                      >
                        Enable review
                      </Button>
                    </HStack>
                  </VStack>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      </Box>
    </Box>
  );
}
